// =====================================================================================
// INVRT Radar - web console - campaign audience action/state hook (AU05-AU08 bridge)
//
// The ONE named audience hook family on the existing useApiClient. It wires the four
// mounted bridge routes exactly as supabase/functions/api/routes/audiences.ts answers
// them (see .ralph/w3-au09ui/DISCOVERY-MANIFEST.md, frozen before the first edit):
//
//   POST /radar-audience/config           read | save-default | clear-default |
//                                         save-override | clear-override   (AU05)
//   POST /radar-audience/export           the pinned attempt generate/replay (AU06)
//   POST /radar-audience/export/history   the read-only attempt projection    (AU06)
//   POST /radar-audience/export/receipts  the read-only safe receipt          (AU08)
//
// THE LAWS THIS HOOK ENFORCES (PRD_03_AUDIENCE.md, UI section):
//   - Render/refetch NEVER generates: reads and status/receipt refreshes post to the
//     config/history/receipts routes only. The export route is posted ONLY by the
//     explicit generate action, and a retry of one original attempt reuses the SAME
//     pinned attempt id (a replay/resume, never a silent new generation).
//   - Org switch cancels: every query key carries actor/org/campaign/client identity
//     and the scope observer's revision; an org switch aborts in-flight reads, resets
//     the pinned attempt id and refuses any resolved answer whose scope changed
//     between click and settle (a stale query can neither display another org's
//     receipt nor send an action with old scope).
//   - Everything arriving is PARSED before display: a response that is not the exact
//     projected shape (or is for another mapped campaign key) never renders.
// =====================================================================================

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { type ApiClient } from "@/lib/api";
import {
  type AudienceAttempt,
  type AudienceConfigAck,
  type AudienceConfigOperation,
  type AudienceExportAck,
  type AudienceManualHandoff,
  type AudiencePlatform,
  type AudienceReceiptsAck,
  type AudienceSegmentation,
  type AudienceEffectiveConfigState,
} from "@/lib/types";

const CONFIG_PATH = "/radar-audience/config";
const EXPORT_PATH = "/radar-audience/export";
const HISTORY_PATH = "/radar-audience/export/history";
const RECEIPTS_PATH = "/radar-audience/export/receipts";

/** The fixed audit reason for the read-only config projection. */
export const AUDIENCE_READ_REASON = "Read the assigned campaign audience configuration.";

/** Thrown when the org scope changed between an action's click and its settle: the
 * answer belongs to a scope the operator has already left, so it is refused locally
 * and never displayed or retried. */
export class AudienceScopeChangedError extends Error {
  constructor() {
    super("The organisation scope changed before this action settled.");
    this.name = "AudienceScopeChangedError";
  }
}

/** The per-client identity (the accepted WeakMap pattern from the read-only tab). */
const clientIds = new WeakMap<ApiClient, number>();
let nextClientId = 0;
function clientId(client: ApiClient): number {
  const known = clientIds.get(client);
  if (known !== undefined) return known;
  const id = ++nextClientId;
  clientIds.set(client, id);
  return id;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function safeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function optionalInteger(value: unknown): value is number {
  return value === null || value === undefined || safeInteger(value);
}

function safeString(value: unknown, max = 400): value is string {
  return typeof value === "string" && value.trim().length > 0 && value.length <= max;
}

function parsePlatform(value: unknown): AudiencePlatform | null {
  return typeof value === "string" &&
    ["meta", "google_contact", "google_address", "tiktok", "linkedin"].includes(value)
    ? (value as AudiencePlatform)
    : null;
}

function parseSegmentation(value: unknown): AudienceSegmentation | null {
  return typeof value === "string" && ["all", "tag", "intent"].includes(value)
    ? (value as AudienceSegmentation)
    : null;
}

/** The strict projection of the config ack: the resolved pair's own state, pinned to
 * the selected campaign key so a stale cross-campaign answer cannot render. */
export function parseAudienceConfigState(
  value: unknown,
  campaignKey: string,
): AudienceEffectiveConfigState {
  if (!isPlainObject(value) || !("state" in value) || !("operation" in value)) {
    throw new Error("Audience configuration cannot be verified.");
  }
  if (value.operation !== "read" && value.operation !== "save-default" &&
      value.operation !== "clear-default" && value.operation !== "save-override" &&
      value.operation !== "clear-override") {
    throw new Error("Audience configuration cannot be verified.");
  }
  if (typeof value.correlation_id !== "string" || value.correlation_id.length === 0) {
    throw new Error("Audience configuration cannot be verified.");
  }
  const state = value.state;
  if (!isPlainObject(state)) throw new Error("Audience configuration cannot be verified.");
  if (
    typeof state.campaign_key !== "string" ||
    state.campaign_key.trim().length === 0 ||
    state.campaign_key.length > 200 ||
    state.campaign_key !== campaignKey ||
    typeof state.organization_id !== "string" ||
    !UUID_PATTERN.test(state.organization_id) ||
    typeof state.configured !== "boolean" ||
    typeof state.setup_required !== "boolean" ||
    typeof state.override_present !== "boolean" ||
    typeof state.override_applied !== "boolean" ||
    !optionalInteger(state.org_version) ||
    !optionalInteger(state.campaign_version)
  ) {
    throw new Error("Audience configuration cannot be verified.");
  }
  let platforms: AudiencePlatform[] | null = null;
  if (state.effective_platforms !== null && state.effective_platforms !== undefined) {
    if (!Array.isArray(state.effective_platforms)) {
      throw new Error("Audience configuration cannot be verified.");
    }
    const parsed = state.effective_platforms.map(parsePlatform);
    if (parsed.some((p) => p === null)) {
      throw new Error("Audience configuration cannot be verified.");
    }
    platforms = parsed as AudiencePlatform[];
  }
  let segmentation: AudienceSegmentation | null = null;
  if (state.effective_segmentation !== null && state.effective_segmentation !== undefined) {
    segmentation = parseSegmentation(state.effective_segmentation);
    if (segmentation === null) throw new Error("Audience configuration cannot be verified.");
  }
  let segmentValues: string[] | null = null;
  if (state.effective_segment_values !== null && state.effective_segment_values !== undefined) {
    if (
      !Array.isArray(state.effective_segment_values) ||
      state.effective_segment_values.some((v) => typeof v !== "string")
    ) {
      throw new Error("Audience configuration cannot be verified.");
    }
    segmentValues = state.effective_segment_values as string[];
  }
  return {
    organization_id: state.organization_id,
    campaign_key: state.campaign_key,
    configured: state.configured,
    setup_required: state.setup_required,
    override_present: state.override_present,
    override_applied: state.override_applied,
    org_version: (state.org_version as number | null) ?? null,
    campaign_version: (state.campaign_version as number | null) ?? null,
    effective_platforms: platforms,
    effective_segmentation: segmentation,
    effective_segment_values: segmentValues,
  };
}

/** The strict projection of an attempt (generate ack, history projection or the
 * receipts' embedded attempt). */
export function parseAudienceAttempt(value: unknown): AudienceAttempt {
  if (!isPlainObject(value)) throw new Error("Audience attempt cannot be verified.");
  if (
    (value.job_id !== null && value.job_id !== undefined &&
      (typeof value.job_id !== "string" || !UUID_PATTERN.test(value.job_id))) ||
    typeof value.status !== "string" ||
    !["committed", "running", "failed", "unknown"].includes(value.status)
  ) {
    throw new Error("Audience attempt cannot be verified.");
  }
  if (
    (value.config_revision !== null && value.config_revision !== undefined &&
      (typeof value.config_revision !== "string" || value.config_revision.length === 0 ||
        value.config_revision.length > 200)) ||
    (value.report_sha256 !== null && value.report_sha256 !== undefined &&
      (typeof value.report_sha256 !== "string" || !/^[0-9a-f]{64}$/.test(value.report_sha256)))
  ) {
    throw new Error("Audience attempt cannot be verified.");
  }
  if (!Array.isArray(value.files)) throw new Error("Audience attempt cannot be verified.");
  const files = value.files.map((row): AudienceAttempt["files"][number] => {
    if (
      !isPlainObject(row) ||
      !safeString(row.platform, 64) ||
      typeof row.sha256 !== "string" ||
      !/^[0-9a-f]{64}$/.test(row.sha256) ||
      !safeInteger(row.byte_count) ||
      !safeInteger(row.row_count)
    ) {
      throw new Error("Audience attempt cannot be verified.");
    }
    return {
      platform: row.platform,
      sha256: row.sha256,
      byte_count: row.byte_count,
      row_count: row.row_count,
    };
  });
  return {
    job_id: (value.job_id as string | null) ?? null,
    status: value.status as AudienceAttempt["status"],
    config_revision: (value.config_revision as string | null) ?? null,
    report_sha256: (value.report_sha256 as string | null) ?? null,
    files,
  };
}

function parseExportAck(value: unknown): AudienceAttempt {
  if (
    !isPlainObject(value) ||
    typeof value.correlation_id !== "string" ||
    value.correlation_id.length === 0 ||
    !("attempt" in value)
  ) {
    throw new Error("Audience attempt cannot be verified.");
  }
  return parseAudienceAttempt(value.attempt);
}

function parseReceiptsAck(value: unknown): AudienceReceiptsAck["receipt"] {
  if (!isPlainObject(value) || typeof value.correlation_id !== "string" ||
      value.correlation_id.length === 0 || !("receipt" in value) || !isPlainObject(value.receipt)) {
    throw new Error("Audience receipt cannot be verified.");
  }
  const receipt = value.receipt;
  const attempt = parseAudienceAttempt(receipt.attempt);
  if (!Array.isArray(receipt.serving)) throw new Error("Audience receipt cannot be verified.");
  const serving = receipt.serving.map((row): AudienceReceiptsAck["receipt"]["serving"][number] => {
    if (
      !isPlainObject(row) ||
      !safeString(row.platform, 64) ||
      !safeInteger(row.minted_tokens) ||
      !safeInteger(row.served_tokens) ||
      !safeString(row.state, 64)
    ) {
      throw new Error("Audience receipt cannot be verified.");
    }
    return {
      platform: row.platform,
      minted_tokens: row.minted_tokens,
      served_tokens: row.served_tokens,
      state: row.state,
    };
  });
  let version: AudienceReceiptsAck["receipt"]["version"] = null;
  if (receipt.version !== null && receipt.version !== undefined) {
    if (!isPlainObject(receipt.version)) throw new Error("Audience receipt cannot be verified.");
    const v = receipt.version;
    if (
      typeof v.version_id !== "string" ||
      !UUID_PATTERN.test(v.version_id) ||
      !safeInteger(v.version_no) ||
      !safeString(v.state, 64) ||
      !safeString(v.state_label, 64)
    ) {
      throw new Error("Audience receipt cannot be verified.");
    }
    let handoff: AudienceManualHandoff | null = null;
    if (v.handoff !== null && v.handoff !== undefined) {
      if (!isPlainObject(v.handoff) || v.handoff.mode !== "manual_recorded") {
        throw new Error("Audience receipt cannot be verified.");
      }
      const h = v.handoff;
      if (
        h.remote_confirmed !== false ||
        !Array.isArray(h.platforms) ||
        h.platforms.some((p) => typeof p !== "string") ||
        (h.recorded_at !== null && h.recorded_at !== undefined &&
          typeof h.recorded_at !== "string") ||
        !safeString(h.note, 500)
      ) {
        throw new Error("Audience receipt cannot be verified.");
      }
      handoff = {
        mode: "manual_recorded",
        remote_confirmed: false,
        platforms: h.platforms as string[],
        recorded_at: (h.recorded_at as string | null) ?? null,
        note: h.note,
      };
    }
    let synced: string[] | null = null;
    if (v.synced_platforms !== null && v.synced_platforms !== undefined) {
      if (!Array.isArray(v.synced_platforms) || v.synced_platforms.some((p) => typeof p !== "string")) {
        throw new Error("Audience receipt cannot be verified.");
      }
      synced = v.synced_platforms as string[];
    }
    let identities: number | null = null;
    if (v.unique_identities !== null && v.unique_identities !== undefined) {
      if (!safeInteger(v.unique_identities)) {
        throw new Error("Audience receipt cannot be verified.");
      }
      identities = v.unique_identities;
    }
    if (v.note !== null && v.note !== undefined && typeof v.note !== "string") {
      throw new Error("Audience receipt cannot be verified.");
    }
    version = {
      version_id: v.version_id,
      version_no: v.version_no,
      state: v.state,
      state_label: v.state_label,
      handoff,
      synced_platforms: synced,
      unique_identities: identities,
      note: (v.note as string | null) ?? null,
    };
  }
  if (
    !isPlainObject(receipt.states) ||
    Object.values(receipt.states).some((label) => typeof label !== "string") ||
    !safeString(receipt.counts_note, 500)
  ) {
    throw new Error("Audience receipt cannot be verified.");
  }
  return {
    attempt,
    serving,
    version,
    states: receipt.states as Record<string, string>,
    counts_note: receipt.counts_note,
  };
}

export interface AudienceConfigSaveInput {
  kind: Extract<AudienceConfigOperation, "save-default" | "clear-default" | "save-override" | "clear-override">;
  platforms?: AudiencePlatform[];
  segmentation?: AudienceSegmentation;
  segmentValues?: string[];
  reason: string;
}

/** The one audience hook: the config read/mutation family, the pinned attempt and its
 * read-only status and receipt projections. */
export function useAudience(campaignId: string, campaignKey: string) {
  const api = useApiClient();
  const { session, orgId, subscribeScopeInvalidation } = useSession();
  const actor = session?.user.id ?? null;
  const owner = useMemo(() => clientId(api), [api]);
  const [revision, setRevision] = useState(0);
  /** The client-pinned attempt identity of the ONE original attempt (per scope). */
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const scopeRef = useRef({ owner, orgId, actor });
  useEffect(() => {
    scopeRef.current = { owner, orgId, actor };
  }, [owner, orgId, actor]);
  useEffect(() => {
    let active = true;
    const unsubscribe = subscribeScopeInvalidation(() => {
      if (!active) return;
      setRevision((current) => current + 1);
      setAttemptId(null);
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [subscribeScopeInvalidation]);
  // A campaign or client change invalidates the pinned attempt exactly like a scope switch.
  useEffect(() => {
    setAttemptId(null);
  }, [owner, orgId, actor, campaignId]);

  const enabled = Boolean(actor && orgId && campaignId && campaignKey);

  const config = useQuery({
    queryKey: ["audience-config", actor, orgId, campaignId, campaignKey, owner, revision],
    enabled,
    queryFn: async ({ signal }) => {
      const ack = await api.post<AudienceConfigAck>(
        CONFIG_PATH,
        {
          operation: "read" as const,
          campaign_id: campaignId,
          reason: AUDIENCE_READ_REASON,
          expected_config_version: 0,
        },
        { signal },
      );
      return parseAudienceConfigState(ack, campaignKey);
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  // A failed read nulls the projected state (the accepted preview's own law: the error
  // replaces the counts). Cached config never drives actions after a refusal, so a
  // stale CAS version can never be spent by a save and generation cannot ride a
  // configuration the service would not confirm.
  const configData = config.isError ? null : (config.data ?? null);

  const saveConfig = useMutation({
    mutationFn: async (input: AudienceConfigSaveInput) => {
      const scope = scopeRef.current;
      const expected =
        input.kind === "save-default" || input.kind === "clear-default"
          ? (configData?.org_version ?? 0)
          : (configData?.campaign_version ?? 0);
      const body = {
        operation: input.kind,
        campaign_id: campaignId,
        reason: input.reason,
        expected_config_version: expected,
        ...(input.platforms ? { platforms: input.platforms } : {}),
        ...(input.segmentation ? { segmentation: input.segmentation } : {}),
        ...(input.segmentValues ? { segment_values: input.segmentValues } : {}),
      };
      const ack = await api.post<AudienceConfigAck>(CONFIG_PATH, body);
      if (
        scopeRef.current.owner !== scope.owner ||
        scopeRef.current.orgId !== scope.orgId ||
        scopeRef.current.actor !== scope.actor
      ) {
        throw new AudienceScopeChangedError();
      }
      return parseAudienceConfigState(ack, campaignKey);
    },
  });

  const generate = useMutation({
    mutationFn: async (explicitId?: string) => {
      const scope = scopeRef.current;
      // One original attempt keeps ONE pinned id: an undefined id mints one (the first
      // start), an explicit id is the user's separate new attempt, and retries of an
      // existing attempt reuse the pinned id as the documented replay/resume identity.
      const id = explicitId ?? attemptId ?? crypto.randomUUID();
      if (attemptId === null) setAttemptId(id);
      const ack = await api.post<AudienceExportAck>(
        EXPORT_PATH,
        { campaign_id: campaignId, attempt_id: id },
      );
      if (
        scopeRef.current.owner !== scope.owner ||
        scopeRef.current.orgId !== scope.orgId ||
        scopeRef.current.actor !== scope.actor
      ) {
        throw new AudienceScopeChangedError();
      }
      setAttemptId(id);
      return parseExportAck(ack);
    },
  });

  const history = useQuery({
    queryKey: ["audience-attempt", actor, orgId, campaignId, campaignKey, owner, revision, attemptId],
    enabled: enabled && attemptId !== null,
    queryFn: async ({ signal }) => {
      const ack = await api.post<AudienceExportAck>(
        HISTORY_PATH,
        { campaign_id: campaignId, attempt_id: attemptId! },
        { signal },
      );
      return parseExportAck(ack);
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const receipts = useQuery({
    queryKey: ["audience-receipts", actor, orgId, campaignId, campaignKey, owner, revision, attemptId],
    enabled: enabled && attemptId !== null,
    queryFn: async ({ signal }) => {
      const ack = await api.post<AudienceReceiptsAck>(
        RECEIPTS_PATH,
        { campaign_id: campaignId, attempt_id: attemptId! },
        { signal },
      );
      return parseReceiptsAck(ack);
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const startNewAttempt = () => setAttemptId(crypto.randomUUID());

  return {
    enabled,
    config,
    configData,
    saveConfig,
    generate,
    attemptId,
    startNewAttempt,
    history,
    receipts,
  };
}
